import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { startCheckout } from "../lib/billing";

/**
 * ResumeCheckout — top-level effect that automatically continues a
 * pending Stripe Checkout after the teacher finishes signing in.
 *
 * Flow:
 *   1. Pricing page stashes `{tier_id, billing_period}` to localStorage
 *      under `atomicedu.pendingCheckout` before triggering Google
 *      sign-in OR opening the email magic-link dialog.
 *   2. After auth completes (Google callback OR magic-link email),
 *      this component sees a logged-in user + the stash, removes the
 *      stash (so it never re-fires), and POSTs to /api/billing/checkout
 *      to grab the Stripe URL.
 *   3. We `window.location.href = ...` straight to Stripe so the teacher
 *      goes from "click email link" → "Stripe checkout" with no
 *      intermediate landing-page detour.
 *
 * localStorage (not sessionStorage) so the stash survives the magic-link
 * "click in email → new tab" hop. Cleared on success and on auth failure.
 */
const STORAGE_KEY = "atomicedu.pendingCheckout";

const readPending = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.tier_id || !parsed?.billing_period) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearPending = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* fine */
  }
};

export const ResumeCheckout = () => {
  const { user, loading } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (fired.current) return;

    const pending = readPending();
    if (!pending) return;

    // Don't auto-resume if the teacher is already actively subscribed —
    // they're not trying to start another trial. Just clear the stash.
    if (user?.subscription?.is_active) {
      clearPending();
      return;
    }

    fired.current = true;
    clearPending();

    (async () => {
      try {
        const { url } = await startCheckout({
          tier_id: pending.tier_id,
          billing_period: pending.billing_period,
        });
        // Brief toast so the teacher knows what's happening — Stripe can
        // take a beat to load and a sudden redirect feels unexplained.
        toast.success("Welcome back — opening Stripe…");
        window.location.href = url;
      } catch (e) {
        const detail =
          e?.response?.data?.detail || e?.message || "Couldn't open checkout";
        toast.error("Couldn't resume checkout", { description: String(detail) });
      }
    })();
  }, [user, loading]);

  return null;
};

export default ResumeCheckout;
